/**
 * Shared types for the long-task protocol (ADR 059).
 *
 * Each MCP tool declares its role under `_meta["corteva.com/longTask"]`; the
 * chain surfaces that block via `POST /chat/tools`. FastMCP injects a sibling
 * `fastmcp` key, so consumers must key into the namespace, not treat `_meta`
 * as single-key.
 */
import type { LongTaskGroup } from "hooks/tasks/useLongTaskGroups";
import type { PendingInjection } from "hooks/tasks/usePendingInjections";
import type { TerminalTaskStatus } from "types/TaskRegistry";

export const LONG_TASK_META_NAMESPACE = "corteva.com/longTask";

export type LongTaskRole = "submit" | "status" | "fetch";

/**
 * Discriminates directives that share a `longTaskJobId` but serve different
 * purposes. Defined here (a leaf module) so both the injection queue and
 * `SyntheticUserMessage` can import it without a cycle through the types barrel.
 */
export type PendingInjectionKind =
  | "long-task-terminal"
  | "long-task-fetch-result"
  | "long-task-check-status"
  | "long-task-retry-fetch"
  | "mcp-task-terminal";

/**
 * The `_meta["corteva.com/longTask"]` block on a single tool.
 * `directiveTemplate` is present only on the submit role; templates interpolate
 * `{group}`, `{jobId}`, `{correlationId}`, and `{reason}`.
 *
 * The block says nothing about the card's label: the submit envelope's
 * `display_name` is where a server names a job, so no key here points the UI at a
 * `tool_input` field.
 */
export interface LongTaskMeta {
  version: 1;
  role: LongTaskRole;
  group: string;
  directiveTemplate?: Partial<Record<TerminalTaskStatus, string>>;
}

/** A tool definition as returned by the chain's `POST /chat/tools`. */
export interface ToolDefinition {
  name: string;
  server: string;
  description?: string;
  inputSchema?: unknown;
  _meta?: Record<string, unknown>;
}

const DEFAULT_DIRECTIVE_TEMPLATE: Record<TerminalTaskStatus, string> = {
  completed:
    "The {group} job (job_id: {jobId}) has completed successfully. Fetch and present the results to the user.",
  failed:
    "The {group} job (job_id: {jobId}) failed: {reason}. Report the failure reason to the user and clear the job from the queue.",
  cancelled:
    "The {group} job (job_id: {jobId}) was cancelled: {reason}. Report this to the user and clear the job from the queue.",
};

const UNTRUSTED_OPEN = "<untrusted-tool-output>";
const UNTRUSTED_CLOSE = "</untrusted-tool-output>";
/** Bounds how much server text one directive can push into the model's context. */
const UNTRUSTED_MAX_LENGTH = 2000;
/**
 * Renders the delimiter inert wherever it appears in a value, so the value cannot
 * close the marker around it and promote its tail to trusted-looking prose.
 *
 * Matches the bare name in any case rather than a tag shape: a tag-shaped pattern
 * has to decide what counts as a tag, and every such decision is a way out of the
 * marked region. One pass suffices because the replacement shares no text with the
 * name, so a substitution cannot splice its neighbours into a fresh occurrence the
 * way a deletion could.
 */
function neutralizeMarkerTokens(value: string): string {
  return value.replace(/untrusted-tool-output/gi, "[marker-removed]");
}

/**
 * Marks MCP-supplied text as untrusted, wrapping it in an explicit
 * `<untrusted-tool-output>` delimiter (ADR 059).
 *
 * A directive is committed as a `role: "user"` message, so anything the server
 * puts in it is indistinguishable from something the user typed. The protocol is
 * deliberately server-agnostic, so a third-party or compromised server could
 * otherwise smuggle agent instructions through a failure reason or a result
 * label. Delimiters in the value are neutralized first, so it cannot close the
 * marker around it.
 *
 * Capping runs after neutralizing, so the cap cannot bisect a delimiter and leave
 * a live fragment behind.
 *
 * The mark is advisory, not enforced: the API still receives the message as an
 * ordinary user turn, so an agent that disregards the mark can still be steered
 * by what is inside it. Returns "" unchanged, so callers can keep testing for
 * empty.
 */
export function markUntrusted(value: string): string {
  if (!value) return "";
  const neutralized = neutralizeMarkerTokens(value);
  const capped =
    neutralized.length > UNTRUSTED_MAX_LENGTH
      ? `${neutralized.slice(0, UNTRUSTED_MAX_LENGTH)}…`
      : neutralized;
  return `${UNTRUSTED_OPEN}${capped}${UNTRUSTED_CLOSE}`;
}

function interpolateDirective(
  template: string,
  vars: {
    groupName: string;
    jobId: string;
    correlationId?: string;
    reason?: string;
  }
): string {
  const reason = vars.reason ?? "";
  // Drop the leading `: ` clause separator when reason is empty so templates
  // don't render "failed: ." with dangling punctuation.
  const base = reason ? template : template.replace(/:?\s*\{reason\}/g, "");
  // The keys below are placeholder names servers write in templates.
  // `reason` is marked untrusted since it's a free-form value.
  const values: Record<string, string> = {
    group: vars.groupName,
    jobId: vars.jobId,
    correlationId: vars.correlationId ?? "",
    reason: markUntrusted(reason),
  };
  // Single pass with a function replacer: each substituted value is inserted
  // literally (so `$&`, `$1`, etc. in a server-supplied reason are not treated
  // as replacement patterns) and is never re-scanned for other placeholders
  // (so a reason containing the literal text `{jobId}` is left intact).
  return base.replace(
    /\{(group|jobId|correlationId|reason)\}/g,
    (_match, key) => values[key]
  );
}

/**
 * Builds the terminal directive based on the submit tool's `directiveTemplate`
 * or built-in defaults.
 *
 * `groupConfig` is the job's lifecycle-map entry, used only for the template.
 * It is resolved per call so a server-side template change reaches in-flight
 * jobs after a reload.
 */
export function buildTerminalDirective(
  groupConfig: LongTaskGroup | undefined,
  status: TerminalTaskStatus,
  job: { groupName: string; jobId: string; correlationId?: string },
  reason?: string | null
): string {
  const template =
    groupConfig?.directiveTemplate?.[status] ??
    DEFAULT_DIRECTIVE_TEMPLATE[status];
  return interpolateDirective(template, {
    groupName: job.groupName,
    jobId: job.jobId,
    correlationId: job.correlationId,
    reason: typeof reason === "string" ? reason.trim() : "",
  });
}

/**
 * Builds a pending injection for a terminal MCP task (ADR 060).
 * The drain in `useChatTasks` commits this as a SyntheticUserMessage that triggers
 * the agent to call get_task_result.
 */
export function buildMcpTaskTerminalInjection(
  taskId: string,
  status: TerminalTaskStatus,
  chatId: string
): PendingInjection {
  const verb =
    status === "completed" ? "completed" : `finished with status: ${status}`;
  return {
    id: crypto.randomUUID(),
    chatId,
    content: `Task ${taskId} has ${verb}. Retrieve the result using get_task_result and continue where you left off.`,
    createdAt: new Date().toISOString(),
    kind: "mcp-task-terminal",
    longTaskJobId: taskId,
  };
}
